"use client";

import { ReactNode, useEffect } from 'react';
import { PageTitleProvider, usePageTitleContext } from '@/context/PageTitleContext';
import { updateDocumentTitle } from '@/utils/config/appConfig';
import { usePathname } from 'next/navigation';
import { ownsItsOwnDocumentTitle } from '@/utils/seo/serverTitledRoutes';

function PublicLayoutInner({ children }: { children: ReactNode }) {
  const { title, gymName } = usePageTitleContext();
  const pathname = usePathname();

  // Update document title whenever context title, gymName, or pathname changes
  useEffect(() => {
    // A server-rendered route already wrote its own <title> through
    // `generateMetadata`. Overwriting it here would replace the gym's real
    // title with one built from the URL — and Googlebot renders, so that is the
    // title it would index. See `serverTitledRoutes`.
    if (ownsItsOwnDocumentTitle(pathname)) return;

    // If no specific title is set in context, generate a fallback from pathname
    let displayTitle = title;
    if (!displayTitle) {
      const parts = pathname.split('/').filter(Boolean);
      // For /programs/[gymSlug], parts[0] is 'programs', parts[1] is [gymSlug]
      const lastPart = parts[parts.length - 1];
      
      if (lastPart) {
        displayTitle = lastPart.charAt(0).toUpperCase() + lastPart.slice(1).replace(/-/g, ' ');
      } else {
        displayTitle = 'Welcome';
      }
    }

    updateDocumentTitle(displayTitle, gymName);
  }, [title, gymName, pathname]);

  return <>{children}</>;
}

export default function PublicLayout({ children }: { children: ReactNode }) {
  return (
    <PageTitleProvider>
      <div 
        className="light min-h-screen bg-gray-50"
        data-theme="light"
        style={{ colorScheme: 'light' }}
      >
        <PublicLayoutInner>
          {children}
        </PublicLayoutInner>
      </div>
    </PageTitleProvider>
  );
}

